import { Component, OnDestroy } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { delay, filter, map, takeUntil, tap } from 'rxjs/operators';
import { v4 as guid } from 'uuid';
import { Animations } from '../../animations';
import { Themes } from '../../constants/themes';
import { ToastEvents } from './toast.events';
import { Toast, ToastEvent, ToastTypes } from './toast.models';

@Component({
    selector: 'toast-alert-component',
    templateUrl: './toast.element.html',
    styleUrls: ['./toast.element.scss'],
    animations: [Animations.slideFromRight],
    standalone: false
})
export class ToastAlertComponent implements OnDestroy {

  private onDestroy$ = new Subject<void>();

  public toasts: Toast[] = [];

  constructor(
  ) {
    fromEvent(window, ToastEvents.TOAST_EVENT_NAME).pipe(
      map((event) => this.generateModel(event as CustomEvent<ToastEvent>)),
      tap(toast => this.toasts = [...this.toasts, toast]),
      filter(toast => toast.type === ToastTypes.SUCCESS),
      delay(5000),
      takeUntil(this.onDestroy$),
      tap(toast => this.toasts = this.toasts.filter(t => t.id !== toast.id)),
      takeUntil(this.onDestroy$),
    ).subscribe()
  }

  private generateModel(event: CustomEvent<ToastEvent>): Toast {
    const theme = getTheme(event.detail.type);

    return {
      id: guid(),
      type: event.detail.type,
      title: event.detail.title,
      description: event.detail.description,
      backgroundColour: this.getBackgroundColour(theme),
      shouldShowClose: theme !== Themes.success,
      borderColour: this.getBorderColour(theme),
      icon: this.getIcon(theme),
      iconColour: this.getIconColour(theme),
      colour: 'text-text',
      errorCode: event.detail.errorCode,
    } as Toast;

    function getTheme(toastType: ToastTypes): Themes {
      switch (toastType) {
        case ToastTypes.SUCCESS:
          return Themes.success;
        case ToastTypes.WARNING:
          return Themes.warning;
        case ToastTypes.ERROR:
          return Themes.error;
        default:
          throw Error(`Unexpected ToastType implemented when trying to get theme: ${toastType}`);
      }
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  public close(toast: Toast): void {
    this.toasts = this.toasts.filter(t => t.id !== toast.id);
  }

  private getBackgroundColour(alertType: Themes): string {
    switch (alertType) {
      case Themes.warning:
        return "bg-warning-50";
      case Themes.error:
        return "bg-error-50";
      case Themes.success:
        return "bg-secondary-50";
      case Themes.warningAlt:
      case Themes.errorAlt:
      case Themes.successAlt:
        return "bg-light";
      default:
        throw Error(`Theme "${alertType}" has no implementation for background colour`);
    }
  }

  private getBorderColour(alertType: Themes): string {
    switch (alertType) {
      case Themes.error:
      case Themes.errorAlt:
        return "border-error-400";
      case Themes.success:
      case Themes.successAlt:
        return "border-success-400";
      case Themes.warning:
      case Themes.warningAlt:
        return "border-warning-500";
      default:
        throw Error(`Theme "${alertType}" has no implementation for border colour`);
    }
  }

  private getIconColour(alertType: Themes): string {
    switch (alertType) {
      case Themes.error:
      case Themes.errorAlt:
        return "text-error-400";
      case Themes.success:
      case Themes.successAlt:
        return "text-success-400";
      case Themes.warning:
      case Themes.warningAlt:
        return "text-warning-500";
      default:
        throw Error(`Theme "${alertType}" has no implementation for text colour`);
    }
  }

  private getIcon(alertType: Themes): string {
    switch (alertType) {
      case Themes.error:
      case Themes.errorAlt:
        return 'fa-kit fa-check-border'
      case Themes.warning:
      case Themes.warningAlt:
        return "fa-kit fa-talos-warning-triangle";
      case Themes.success:
      case Themes.successAlt:
        return "fa-kit fa-talos-info-tag";
      default:
        throw Error(`Theme "${alertType}" has no implementation for icon`);
    }
  }

  public trackByToastId(index: number, toast: Toast): string {
    return toast.id;
  }
}
